'use client';

import { Children, isValidElement, useRef, useState, useLayoutEffect, type ReactNode, type ReactElement } from 'react';
import {
    countFlexWrapRowsFromWidths,
    measureSubcategoryTagWidths,
} from '@/components/eataly-category-plp-header/subcategory-tags-layout';
import { cn } from '@/lib/utils';

interface SubcategoryTagsContainerProps {
    children: ReactNode;
    className?: string;
}

function TagItem({ children }: { children: ReactNode }): ReactElement {
    return (
        <div data-tag-item="true" className="shrink-0 snap-center">
            {children}
        </div>
    );
}

export function SubcategoryTagsContainer({ children, className }: SubcategoryTagsContainerProps): ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [childWidths, setChildWidths] = useState<number[]>([]);

    const childrenArray = Children.toArray(children).filter(isValidElement);
    const childrenSignature = childrenArray.map((child) => child.key ?? '').join('|');

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const childObservers: ResizeObserver[] = [];

        const measure = () => {
            setContainerWidth(container.clientWidth);
            setChildWidths(measureSubcategoryTagWidths(container));
        };

        const containerObserver = new ResizeObserver(measure);
        containerObserver.observe(container);

        const observeChildren = () => {
            childObservers.forEach((observer) => observer.disconnect());
            childObservers.length = 0;

            container.querySelectorAll<HTMLElement>('[data-tag-item="true"]').forEach((node) => {
                const observer = new ResizeObserver(measure);
                observer.observe(node);
                childObservers.push(observer);

                const child = node.firstElementChild;
                if (child instanceof HTMLElement) {
                    const childObserver = new ResizeObserver(measure);
                    childObserver.observe(child);
                    childObservers.push(childObserver);
                }
            });
        };

        measure();
        observeChildren();

        const mutationObserver = new MutationObserver(() => {
            measure();
            observeChildren();
        });
        mutationObserver.observe(container, { childList: true, subtree: true });

        return () => {
            containerObserver.disconnect();
            mutationObserver.disconnect();
            childObservers.forEach((observer) => observer.disconnect());
        };
    }, [childrenSignature]);

    const rowsNeeded = countFlexWrapRowsFromWidths(containerWidth, childWidths);
    const needsOverflow = rowsNeeded > 2;

    const halfIndex = Math.ceil(childrenArray.length / 2);
    const row1 = childrenArray.slice(0, halfIndex);
    const row2 = childrenArray.slice(halfIndex);

    return (
        <div
            ref={containerRef}
            data-testid="plp-subcategory-tags"
            data-overflow-mode={needsOverflow ? 'scroll' : 'wrap'}
            className={cn(
                'w-full min-w-0',
                needsOverflow
                    ? '[scrollbar-width:thin] [scrollbar-color:var(--brown-10)_var(--sand-90)] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-[var(--sand-90)] [&::-webkit-scrollbar-thumb]:bg-[var(--brown-10)] [&::-webkit-scrollbar-thumb]:rounded-full overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x snap-x snap-mandatory scroll-smooth'
                    : 'flex flex-wrap gap-2',
                className
            )}>
            {needsOverflow ? (
                <div className="flex w-max min-w-full flex-col items-start gap-2 pb-3">
                    <div className="flex flex-row flex-nowrap gap-2">
                        {row1.map((child) => (
                            <TagItem key={`r1-${String(child.key)}`}>{child}</TagItem>
                        ))}
                    </div>
                    <div className="flex flex-row flex-nowrap gap-2">
                        {row2.map((child) => (
                            <TagItem key={`r2-${String(child.key)}`}>{child}</TagItem>
                        ))}
                    </div>
                </div>
            ) : (
                childrenArray.map((child) => <TagItem key={`wrap-${String(child.key)}`}>{child}</TagItem>)
            )}
        </div>
    );
}
